/**
 * Phase 8 T-06 §7 F + T-06c §21.B / §21.F — Smart Match canvas middle column.
 *
 * Renders the active tab's (Sourcing / Shortlisted / Hidden) vertical stack
 * of `MatchCard`s. Per §21.B the card action set changes per tab; per
 * §21.F the Shortlisted tab (and any collection view) exposes the
 * `[+ Add candidate manually]` inline row beneath a 36 px toolbar.
 *
 * Owns scroll + focus-scroll behaviour. Parent owns the data, focus state,
 * and service wiring. Save-vs-Shortlist label is resolved here via the
 * thread-level `collection` prop.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Inbox,
  Loader2,
  Plus,
  Star,
  StarOff,
} from 'lucide-react';
import type { MatchResult } from '../types';
import { MatchCard } from './MatchCard';
import {
  AddCandidateInlineRow,
  type ManualCandidateInput,
} from './AddCandidateInlineRow';
import type { SmartMatchCollectionRef } from '../lib/smartMatchThreads';
import { shortlistLabelFor } from '../lib/shortlistSemantic';
import { prettifyChannel } from '../lib/matchDisplay';

export type CanvasTab = 'sourcing' | 'shortlisted' | 'hidden';
export type CanvasViewMode = 'grid' | 'list';
/**
 * 21-Apr audit close-out (docs/smart-match/12_DATAFLOW §13.1 — grouping).
 * `none` = flat list (legacy behaviour). `client` = one section per
 * client_id. `job` = one section per job_id. `channel` = one section per
 * `candidate.source_channel`. `channel:Naukri` = filter + group by a
 * specific channel name. Grouping is a presentational concern only —
 * the rank is still computed over the full sorted set before grouping.
 */
export type CanvasGroupBy = 'none' | 'client' | 'job' | 'channel' | string;

interface MatchStackColumnProps {
  matches: MatchResult[];
  /**
   * Per-candidate historical top-job list from `ggj_b2b_match_results`.
   * Used as a fallback for the `Also a fit for` card footer when the
   * in-memory N×M alternates are empty (single-JD search). The layout
   * resolves this via `useAlternateJobsForCandidates` and passes the
   * whole map in; we slice + dedupe per card. Optional — when missing
   * the footer stays hidden exactly like before.
   */
  historicalAlternatesByCandidate?: Map<string, MatchResult[]>;
  /**
   * Layout mode for the card stack (post user-feedback, 21-Apr).
   *   - `'grid'` (default): responsive `auto-fill minmax(300px, 1fr)`
   *     grid that produces 2+ cards per row when the middle column
   *     has room.
   *   - `'list'`: single column. Preferred when scanning candidates
   *     row-by-row, or on narrower viewports where cards would be
   *     squished.
   * Toolbar owns the toggle; parent persists the choice in
   * `localStorage` so it survives reloads.
   */
  viewMode?: CanvasViewMode;
  /**
   * Group mode for the Sourcing stack. Only applied when `tab === 'sourcing'`
   * and `viewMode === 'grid'`; list view + Shortlisted / Hidden tabs always
   * render flat (the sort order there is user-action, not score, so
   * grouping by client / job would confuse the mental model).
   */
  groupBy?: CanvasGroupBy;
  tab: CanvasTab;
  focusedKey: string | null;
  shortlistedKeys: Set<string>;
  /**
   * Keys the user has flagged as "seen" on the Shortlisted tab.
   * The card renders a Seen badge and the button label flips to
   * "Mark as unseen" so the state is reversible.
   */
  viewedKeys?: Set<string>;
  /**
   * Signalled by the parent when the Shortlisted list renders empty
   * purely because the "Hide seen" toggle is on — switches the empty
   * state copy to point the user back at the toolbar toggle.
   */
  shortlistedEmptyReason?: 'hide-seen';
  busyKey: string | null;
  busyKind: 'shortlist' | 'hide' | 'remove' | 'restore' | null;
  loading: boolean;
  collection: SmartMatchCollectionRef | null;
  onFocus: (m: MatchResult) => void;
  onOpen: (m: MatchResult) => void;
  onShortlist: (m: MatchResult) => void;
  onHide: (m: MatchResult) => void;
  onMarkAsSeen?: (m: MatchResult) => void;
  onRemove?: (m: MatchResult) => void;
  onRestore?: (m: MatchResult) => void;
  onAttachCollection?: (collection: SmartMatchCollectionRef) => void;
  onAddAllToList?: () => void;
  onAddCandidateManually?: (input: ManualCandidateInput) => void | Promise<void>;
  addCandidateBusy?: boolean;
  onUpdateSearch: () => void;
}

function matchKey(m: MatchResult): string {
  return `${m.candidate_id}-${m.job_id}`;
}

function EmptyState({
  tab,
  onUpdateSearch,
  emptyReason,
}: {
  tab: CanvasTab;
  onUpdateSearch: () => void;
  /**
   * Context-specific reason for the empty state. Used on the Shortlisted
   * tab to distinguish between "you have not shortlisted anyone yet" and
   * "every shortlisted candidate is marked as seen and Hide seen is on".
   */
  emptyReason?: 'hide-seen' | 'remove-or-seen';
}) {
  const copy =
    tab === 'shortlisted'
      ? emptyReason === 'hide-seen'
        ? {
            title: 'Everyone on your shortlist is already marked as seen.',
            subtitle: 'Toggle "Show seen" in the toolbar to bring them back.',
            cta: null as string | null,
          }
        : emptyReason === 'remove-or-seen'
          ? {
              title: 'No unseen candidates here right now.',
              subtitle:
                'Use the Sourcing tab to shortlist more, or clear the "Hide seen" toggle to review ones you already looked at.',
              cta: null,
            }
          : {
              title: 'Nothing shortlisted yet.',
              subtitle:
                'Press S on a focused card or click Shortlist to save candidates here.',
              cta: null,
            }
      : tab === 'hidden'
        ? {
            title: 'Hidden candidates will appear here.',
            subtitle: 'Press H or click Hide on a card to move it out of sourcing.',
            cta: null,
          }
        : {
            title: 'No matches for this search.',
            subtitle: 'Try broadening the criteria — more seniority levels, more locations, or a shorter must-have list.',
            cta: 'Update search',
          };

  return (
    <div
      className="mx-auto flex max-w-[360px] flex-col items-center gap-3 py-12 text-center"
      role="status"
    >
      <div
        className="flex h-10 w-10 items-center justify-center rounded-full"
        style={{
          background: 'var(--ggj-accent-soft,#D1FAE5)',
          color: '#065F46',
        }}
        aria-hidden
      >
        <Inbox className="h-5 w-5" />
      </div>
      <p
        className="text-[13px] font-medium"
        style={{ color: 'var(--vscode-text-primary)' }}
      >
        {copy.title}
      </p>
      <p
        className="text-[12px] leading-relaxed"
        style={{ color: 'var(--vscode-text-secondary)' }}
      >
        {copy.subtitle}
      </p>
      {copy.cta ? (
        <button
          type="button"
          onClick={onUpdateSearch}
          className="mt-1 inline-flex h-8 items-center rounded-[10px] border px-3 text-[12px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
          style={{
            borderColor: 'var(--vscode-border-primary)',
            color: 'var(--vscode-text-primary)',
          }}
        >
          {copy.cta}
        </button>
      ) : null}
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div
      className="grid gap-3"
      style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}
      aria-busy="true"
    >
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="animate-pulse rounded-[10px] border p-4"
          style={{
            borderColor: 'var(--vscode-border-primary)',
            background: 'var(--vscode-bg-elevated)',
          }}
        >
          <div className="mb-3 flex items-center gap-3">
            <div
              className="h-7 w-7 shrink-0 rounded-full"
              style={{ background: 'var(--vscode-bg-hover)' }}
            />
            <div className="flex-1 space-y-2">
              <div
                className="h-3 w-2/3 rounded"
                style={{ background: 'var(--vscode-bg-hover)' }}
              />
              <div
                className="h-2 w-1/3 rounded"
                style={{ background: 'var(--vscode-bg-hover)' }}
              />
            </div>
            <div
              className="h-5 w-12 rounded-full"
              style={{ background: 'var(--vscode-bg-hover)' }}
            />
          </div>
          <div className="space-y-1.5">
            <div className="h-2 rounded" style={{ background: 'var(--vscode-bg-hover)' }} />
            <div className="h-2 w-5/6 rounded" style={{ background: 'var(--vscode-bg-hover)' }} />
            <div className="h-2 w-2/3 rounded" style={{ background: 'var(--vscode-bg-hover)' }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function MatchStackColumn({
  matches,
  historicalAlternatesByCandidate,
  viewMode = 'grid',
  groupBy = 'none',
  tab,
  focusedKey,
  shortlistedKeys,
  viewedKeys,
  shortlistedEmptyReason,
  busyKey,
  busyKind,
  loading,
  collection,
  onFocus,
  onOpen,
  onShortlist,
  onHide,
  onMarkAsSeen,
  onRemove,
  onRestore,
  onAttachCollection,
  onAddAllToList,
  onAddCandidateManually,
  addCandidateBusy = false,
  onUpdateSearch,
}: MatchStackColumnProps) {
  const cardRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());
  const [addRowOpen, setAddRowOpen] = useState(false);

  // Pagination state — only consumed by list mode, but kept outside
  // the mode branch so the user doesn't lose their page size when
  // toggling views. Page size is persisted in `localStorage` per
  // user-feedback (21-Apr) so it sticks across reloads.
  const [pageSize, setPageSize] = useState<number>(() => readPageSize());
  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(matches.length / pageSize));
  const pagedMatches = useMemo(
    () => matches.slice(page * pageSize, (page + 1) * pageSize),
    [matches, page, pageSize],
  );

  /**
   * Build the grouped buckets for grid view. Only the Sourcing tab
   * honours `groupBy` (see type comment); other tabs always fall
   * through to a single "All" bucket which renders flat. Each card
   * retains its `globalIdx` so rank badges stay consistent with the
   * full sorted set regardless of section.
   */
  type GroupBucket = {
    key: string;
    label: string;
    items: Array<{ match: MatchResult; globalIdx: number }>;
  };
  const groupedBuckets = useMemo<GroupBucket[]>(() => {
    /**
     * 21-Apr-26 — Grouping is valid on any tab that renders a
     * `MatchResult[]` grid, not only `sourcing`. Case B (resumes vs
     * real org jobs) routes results to the `high / medium / low`
     * tabs and those also benefit from client/JD grouping so the user
     * can answer "which of our clients does this resume fit best?".
     * `unparseable` is excluded because it renders resumes, not
     * matches.
     *
     * Grid mode uses the paged slice so only `pageSize` cards are
     * rendered at a time. Global rank indices are offset by
     * `page * pageSize` so rank badges still show the candidate's
     * position in the full sorted set, not within the current page.
     */
    const eligible =
      tab !== 'unparseable' && groupBy !== 'none' && groupBy !== '';
    // Grid mode paginates; list mode paginates via its own table row.
    const source = viewMode === 'grid' ? pagedMatches : matches;
    const globalOffset = viewMode === 'grid' ? page * pageSize : 0;
    if (!eligible) {
      return [
        {
          key: '__all__',
          label: 'All',
          items: source.map((m, i) => ({ match: m, globalIdx: globalOffset + i })),
        },
      ];
    }
    const out = new Map<string, GroupBucket>();
    source.forEach((m, i) => {
      let bucketKey: string;
      let bucketLabel: string;
      if (groupBy === 'client') {
        bucketKey =
          (m as { client_id?: string | null }).client_id ??
          (m.job as { client_id?: string | null } | undefined)?.client_id ??
          '__no_client__';
        bucketLabel =
          bucketKey === '__no_client__'
            ? 'No client'
            : ((m.job as { client?: { name?: string | null } | null } | undefined)
                ?.client?.name ??
              (m as { client_name?: string | null }).client_name ??
              `Client ${bucketKey.slice(0, 8)}`);
      } else if (groupBy === 'channel' || groupBy.startsWith('channel:')) {
        // Check both source_channel and external_source (for external sourcing results)
        const candidateAny = m.candidate as Record<string, unknown> | undefined;
        const ch = m.candidate?.source_channel
          || (candidateAny?.external_source as string | undefined)
          || null;
        bucketKey = (ch && typeof ch === 'string' && ch.trim()) ? ch : '__no_channel__';
        bucketLabel =
          bucketKey === '__no_channel__'
            ? 'No source channel'
            : prettifyChannel(bucketKey);
      } else {
        const jid = m.job_id ?? '';
        const isSynthetic =
          !jid ||
          jid === '__prompt__' ||
          jid === '__bulk__' ||
          jid === '__org_jobs__';
        bucketKey = isSynthetic ? '__no_jd__' : jid;
        bucketLabel = isSynthetic
          ? 'No JD attached'
          : (m.job?.title ?? `JD ${jid.slice(0, 8)}`);
      }
      let bucket = out.get(bucketKey);
      if (!bucket) {
        bucket = { key: bucketKey, label: bucketLabel, items: [] };
        out.set(bucketKey, bucket);
      }
      bucket.items.push({ match: m, globalIdx: globalOffset + i });
    });
    return Array.from(out.values()).sort(
      (a, b) => b.items.length - a.items.length,
    );
  }, [matches, pagedMatches, page, pageSize, tab, groupBy, viewMode]);

  /**
   * 21-Apr-26 — feedback "group by client and job is not working".
   *
   * The previous gate required `groupedBuckets.length > 1` so the
   * section headers only rendered when the user had at least two
   * clients / JDs in the result set. In practice every single-JD
   * search collapses to one bucket, so the gate silently hid the
   * headers and made grouping look broken.
   *
   * We now render the header whenever the user has explicitly picked
   * a grouping — even with one bucket — so the affordance matches the
   * selector state. The 1-bucket case also answers "which JD is this
   * stack scored against?" on single-JD searches, which is useful in
   * its own right.
   */
  const groupsActive =
    tab !== 'unparseable' &&
    groupBy !== 'none' &&
    groupBy !== '' &&
    viewMode === 'grid' &&
    groupedBuckets.length >= 1;

  /**
   * 21-Apr — "recommendation = other roles this candidate fits" (N×M
   * only makes sense when we can surface alternates). Index once and
   * pass the pre-sliced list to each card so the render path stays
   * O(1) per card.
   */
  const alternatesByCandidate = useMemo(() => {
    const byCand = new Map<string, MatchResult[]>();
    for (const m of matches) {
      const arr = byCand.get(m.candidate_id) ?? [];
      arr.push(m);
      byCand.set(m.candidate_id, arr);
    }
    for (const arr of byCand.values()) {
      arr.sort((a, b) => b.composite_score - a.composite_score);
    }
    return byCand;
  }, [matches]);

  useEffect(() => {
    if (!focusedKey) return;
    const node = cardRefs.current.get(focusedKey);
    node?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [focusedKey]);

  // Close the add-candidate row when the user switches away.
  useEffect(() => {
    setAddRowOpen(false);
  }, [tab]);

  // Clamp pagination if the dataset shrinks below the current page.
  useEffect(() => {
    if (page > 0 && page >= totalPages) setPage(totalPages - 1);
  }, [page, totalPages]);

  // Reset to the first page when the tab or result set churns hard.
  useEffect(() => {
    setPage(0);
  }, [tab]);

  const showShortlistToolbar =
    tab === 'shortlisted' && matches.length > 0 && onAddCandidateManually;
  const showAddAll =
    tab === 'shortlisted' && matches.length > 0 && !collection && onAddAllToList;

  return (
    <div
      className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-4"
      role="region"
      aria-label="Match stack"
    >
      {showAddAll ? (
        <div className="mb-3">
          <button
            type="button"
            onClick={onAddAllToList}
            className="inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-[10px] border px-3 text-[12px] font-medium transition-colors hover:bg-[var(--ggj-accent-soft,#D1FAE5)]"
            style={{
              borderColor: 'var(--ggj-accent,#10B981)',
              color: '#065F46',
            }}
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Add All to List
          </button>
        </div>
      ) : null}

      {showShortlistToolbar ? (
        <div
          className="mb-3"
          // Reserve the toolbar row vertically so expanding the inline row
          // doesn't push subsequent cards (§21.H CLS < 0.02).
          style={{ minHeight: 36 }}
        >
          <AddCandidateInlineRow
            open={addRowOpen}
            onOpen={() => setAddRowOpen(true)}
            onClose={() => setAddRowOpen(false)}
            busy={addCandidateBusy}
            onSubmit={async (payload) => {
              await onAddCandidateManually?.(payload);
              setAddRowOpen(false);
            }}
          />
        </div>
      ) : null}

      {loading ? (
        <LoadingSkeleton />
      ) : matches.length === 0 ? (
        <EmptyState
          tab={tab}
          onUpdateSearch={onUpdateSearch}
          {...(shortlistedEmptyReason ? { emptyReason: shortlistedEmptyReason } : {})}
        />
      ) : viewMode === 'list' ? (
        /*
         * List mode (post user-feedback, 21-Apr): render matches as a
         * proper data table — fixed header, column labels, and
         * pagination with a selectable page size. Much denser than
         * cards when the user wants to scan a large set. Actions
         * (Save / Hide / Open) live in the last column.
         */
        <>
          <MatchTable
            matches={pagedMatches}
            tab={tab}
            focusedKey={focusedKey}
            shortlistedKeys={shortlistedKeys}
            viewedKeys={viewedKeys}
            busyKey={busyKey}
            busyKind={busyKind}
            collection={collection}
            onFocus={onFocus}
            onOpen={onOpen}
            onShortlist={onShortlist}
            onHide={onHide}
            onMarkAsSeen={onMarkAsSeen}
            onRemove={onRemove}
            onRestore={onRestore}
            rowRefs={cardRefs}
            startRank={page * pageSize}
          />
          <PaginationFooter
            total={matches.length}
            page={page}
            pageSize={pageSize}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={(n) => {
              setPageSize(n);
              writePageSize(n);
              setPage(0);
            }}
          />
          {busyKey && busyKind === 'shortlist' ? (
            <div
              className="mt-2 flex items-center justify-center gap-2 py-1 text-[11px]"
              style={{ color: 'var(--vscode-text-tertiary)' }}
              aria-live="polite"
            >
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden />{' '}
              {shortlistLabelFor(collection)}ing…
            </div>
          ) : null}
        </>
      ) : (
        /*
         * Grid mode: responsive `auto-fill + minmax(300px, 1fr)` so
         * the middle column packs 2+ cards per row on xl screens
         * (and more when the side columns are hidden) while
         * gracefully collapsing to 1 column on narrower breakpoints.
         *
         * Grouped mode (post 21-Apr audit): when `groupBy !== 'none'`
         * and there's more than one bucket, each bucket gets its
         * own `<section>` with a header — otherwise the layout is
         * unchanged from the flat-grid default.
         */
        <div className="flex flex-col gap-4">
          {groupedBuckets.map((bucket) => (
            <section key={bucket.key} className="flex flex-col gap-2">
              {groupsActive ? (
                <header
                  className="flex items-center gap-2"
                  style={{ color: 'var(--vscode-text-secondary)' }}
                >
                  <span className="text-[11px] font-semibold uppercase tracking-wider">
                    {bucket.label}
                  </span>
                  <span
                    className="inline-flex h-4 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold tabular-nums"
                    style={{
                      background: 'var(--vscode-bg-elevated)',
                      color: 'var(--vscode-text-tertiary)',
                      border: '1px solid var(--vscode-border-primary)',
                    }}
                  >
                    {bucket.items.length}
                  </span>
                  <span
                    className="ml-1 flex-1"
                    style={{
                      height: 1,
                      background: 'var(--vscode-border-primary)',
                    }}
                    aria-hidden
                  />
                </header>
              ) : null}
              <div
                className="grid gap-3"
                style={{
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                }}
              >
                {bucket.items.map(({ match: m, globalIdx }) => {
                  const key = matchKey(m);
                  // Skip alternates for external sourcing cards (no real job context yet)
                  const isExternalSourcingCard = !m.job_id || m.job_id === '' ||
                    m.id.startsWith('ext-');
                  let alternates: MatchResult[] = [];
                  if (!isExternalSourcingCard) {
                    // Alternate roles for THIS candidate, excluding the
                    // JD this card is scored against. Top-3 keeps the
                    // footer compact while still answering "what else
                    // could I consider them for?".
                    const liveAlts =
                      (alternatesByCandidate.get(m.candidate_id) ?? [])
                        .filter((alt) => alt.job_id !== m.job_id && alt.job_id);
                    // Fallback for single-JD searches — surface the
                    // candidate's persisted top jobs from
                    // `ggj_b2b_match_results` so the footer still renders
                    // when the current search has only one role
                    // (post-user-feedback 21-Apr-26).
                    const historicalAlts =
                      (historicalAlternatesByCandidate?.get(m.candidate_id) ?? [])
                        .filter((alt) => alt.job_id !== m.job_id && alt.job_id);
                    // Dedupe by job_id AND job title to avoid showing same role twice
                    const seenJobIds = new Set<string>();
                    const seenTitles = new Set<string>();
                    alternates = [...liveAlts, ...historicalAlts]
                      .filter((alt) => {
                        if (!alt.job_id || seenJobIds.has(alt.job_id)) return false;
                        const title = alt.job?.title?.toLowerCase().trim() ?? '';
                        if (title && seenTitles.has(title)) return false;
                        seenJobIds.add(alt.job_id);
                        if (title) seenTitles.add(title);
                        return true;
                      })
                      .slice(0, 3);
                  }
                  return (
                    <MatchCard
                      key={key}
                      ref={(el) => {
                        cardRefs.current.set(key, el);
                      }}
                      match={m}
                      alternateMatches={alternates}
                      variant={tab}
                      // Rank uses `globalIdx + 1` so the 1-based rank
                      // inside each section still reflects the card's
                      // position in the full sorted set (grouping is
                      // purely presentational).
                      rank={tab === 'sourcing' ? globalIdx + 1 : undefined}
                      focused={focusedKey === key}
                      shortlisted={shortlistedKeys.has(key)}
                      viewed={viewedKeys?.has(key) ?? false}
                      collection={collection}
                      shortlistBusy={busyKey === key && busyKind === 'shortlist'}
                      hideBusy={busyKey === key && busyKind === 'hide'}
                      removeBusy={
                        busyKey === key &&
                        (busyKind === 'remove' || busyKind === 'restore')
                      }
                      onFocus={onFocus}
                      onOpen={onOpen}
                      onShortlist={onShortlist}
                      onHide={onHide}
                      onAttachCollection={onAttachCollection}
                      onMarkAsSeen={onMarkAsSeen}
                      onRemove={onRemove}
                      onRestore={onRestore}
                    />
                  );
                })}
              </div>
            </section>
          ))}
          {busyKey && busyKind === 'shortlist' ? (
            <div
              className="col-span-full flex items-center justify-center gap-2 py-1 text-[11px]"
              style={{ color: 'var(--vscode-text-tertiary)' }}
              aria-live="polite"
            >
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden />{' '}
              {shortlistLabelFor(collection)}ing…
            </div>
          ) : null}
          <PaginationFooter
            total={matches.length}
            page={page}
            pageSize={pageSize}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={(n) => {
              setPageSize(n);
              writePageSize(n);
              setPage(0);
            }}
          />
        </div>
      )}
    </div>
  );
}

// ── List view: tabular data grid with pagination ──────────────────────────

const PAGE_SIZE_OPTIONS = [12, 24, 48, 100];
const LS_PAGE_SIZE = 'ggj.canvas.list-page-size';
function readPageSize(): number {
  try {
    const v = Number(localStorage.getItem(LS_PAGE_SIZE));
    if (PAGE_SIZE_OPTIONS.includes(v)) return v;
  } catch {
    /* ignore */
  }
  return 12;
}
function writePageSize(n: number): void {
  try {
    localStorage.setItem(LS_PAGE_SIZE, String(n));
  } catch {
    /* ignore */
  }
}

interface MatchTableProps {
  matches: MatchResult[];
  tab: CanvasTab;
  focusedKey: string | null;
  shortlistedKeys: Set<string>;
  viewedKeys?: Set<string>;
  busyKey: string | null;
  busyKind: 'shortlist' | 'hide' | 'remove' | 'restore' | null;
  collection: SmartMatchCollectionRef | null;
  onFocus: (m: MatchResult) => void;
  onOpen: (m: MatchResult) => void;
  onShortlist: (m: MatchResult) => void;
  onHide: (m: MatchResult) => void;
  onMarkAsSeen?: (m: MatchResult) => void;
  onRemove?: (m: MatchResult) => void;
  onRestore?: (m: MatchResult) => void;
  rowRefs: React.MutableRefObject<Map<string, HTMLDivElement | null>>;
  /**
   * 0-based offset of the first row within the full sorted result set.
   * Passed by the parent so rank numbers stay stable across pages
   * (rank column shows `startRank + idx + 1`). Defaults to 0 when the
   * table is rendered without pagination.
   */
  startRank?: number;
}

function MatchTable({
  matches,
  tab,
  focusedKey,
  shortlistedKeys,
  viewedKeys,
  busyKey,
  busyKind,
  collection,
  onFocus,
  onOpen,
  onShortlist,
  onHide,
  onMarkAsSeen,
  onRemove,
  onRestore,
  rowRefs,
  startRank = 0,
}: MatchTableProps) {
  const saveLabel = shortlistLabelFor(collection);
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[900px] text-left text-[12px]">
        <thead
          className="sticky top-0 z-10 text-[11px] uppercase tracking-wider"
          style={{
            background: 'var(--vscode-bg-elevated)',
            color: 'var(--vscode-text-tertiary)',
          }}
        >
          <tr>
            {tab === 'sourcing' ? <Th className="w-[44px] text-right">#</Th> : null}
            <Th>Candidate</Th>
            <Th>Role / Company</Th>
            <Th className="text-right">Score</Th>
            <Th>Matched skills</Th>
            <Th>Missing skills</Th>
            <Th>Location</Th>
            <Th>Recommendation</Th>
            <Th className="text-right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {matches.map((m, idx) => {
            const key = `${m.candidate_id}-${m.job_id}`;
            const isFocused = focusedKey === key;
            const shortlisted = shortlistedKeys.has(key);
            const seen = viewedKeys?.has(key) ?? false;
            // Accept both 0-1 (legacy persisted) and 0-100 (prompt
            // search output) so the column always reads as a clean %.
            const rawScore = Number.isFinite(m.composite_score)
              ? Number(m.composite_score)
              : 0;
            const score = Math.round(
              Math.max(0, Math.min(100, rawScore > 1 ? rawScore : rawScore * 100)),
            );
            // 1-based rank within the full sorted result set (not just
            // the current page) — only emitted on the Sourcing tab.
            const rank = tab === 'sourcing' ? startRank + idx + 1 : null;
            return (
              <tr
                key={key}
                ref={(el) => {
                  // Cast keeps a single map for both card + row refs.
                  rowRefs.current.set(key, el as unknown as HTMLDivElement | null);
                }}
                onClick={() => onFocus(m)}
                className={`cursor-pointer border-t transition-colors ${
                  isFocused ? 'bg-sky-50 dark:bg-sky-950' : 'hover:bg-[var(--vscode-bg-hover)]'
                }`}
                style={{ borderColor: 'var(--vscode-border-primary)' }}
              >
                {rank != null ? (
                  <Td className="text-right tabular-nums">
                    <span
                      className="font-semibold"
                      style={{ color: '#0369A1' }}
                      title={`Rank #${rank}`}
                    >
                      #{rank}
                    </span>
                  </Td>
                ) : null}
                <Td>
                  <div className="flex items-center gap-2">
                    <div
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold uppercase"
                      style={{
                        background: '#E0F2FE',
                        color: '#0369A1',
                      }}
                      aria-hidden
                    >
                      {initials(m.candidate?.full_name ?? '?')}
                    </div>
                    <div className="min-w-0">
                      <div
                        className="flex items-center gap-1.5 truncate font-medium"
                        style={{ color: 'var(--vscode-text-primary)' }}
                      >
                        <span className="truncate">
                          {m.candidate?.full_name ?? 'Unknown'}
                        </span>
                        {seen && tab === 'shortlisted' ? (
                          <span
                            className="inline-flex h-4 shrink-0 items-center gap-0.5 rounded-full px-1.5 text-[9px] font-semibold uppercase tracking-wider"
                            style={{
                              background: '#E0F2FE',
                              color: '#0369A1',
                            }}
                            title="Marked as seen"
                          >
                            Seen
                          </span>
                        ) : null}
                      </div>
                      {m.candidate?.email ? (
                        <div
                          className="truncate text-[11px]"
                          style={{ color: 'var(--vscode-text-tertiary)' }}
                        >
                          {m.candidate.email}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </Td>
                <Td>
                  <div
                    className="truncate"
                    style={{ color: 'var(--vscode-text-primary)' }}
                  >
                    {m.candidate?.current_title ?? '—'}
                  </div>
                  {m.candidate?.current_company ? (
                    <div
                      className="truncate text-[11px]"
                      style={{ color: 'var(--vscode-text-tertiary)' }}
                    >
                      {m.candidate.current_company}
                    </div>
                  ) : null}
                </Td>
                <Td className="text-right">
                  <span
                    className="inline-flex h-5 min-w-[36px] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold tabular-nums"
                    style={{
                      background:
                        score >= 75
                          ? '#E0F2FE'
                          : score >= 50
                            ? '#FEF3C7'
                            : '#F1F5F9',
                      color:
                        score >= 75 ? '#0369A1' : score >= 50 ? '#92400E' : '#64748B',
                    }}
                  >
                    {score}%
                  </span>
                </Td>
                <Td>
                  <ChipStack items={m.matched_skills ?? []} limit={3} tone="ok" />
                </Td>
                <Td>
                  <ChipStack items={m.missing_skills ?? []} limit={3} tone="warn" />
                </Td>
                <Td>
                  <span
                    className="truncate"
                    style={{ color: 'var(--vscode-text-secondary)' }}
                  >
                    {m.candidate?.location ?? '—'}
                  </span>
                </Td>
                <Td>
                  <span
                    className="truncate"
                    style={{ color: 'var(--vscode-text-secondary)' }}
                  >
                    {m.recommendation ?? '—'}
                  </span>
                </Td>
                <Td className="text-right">
                  <div className="inline-flex items-center gap-1">
                    {tab === 'hidden' ? (
                      <IconAction
                        onClick={(e) => {
                          e.stopPropagation();
                          onRestore?.(m);
                        }}
                        busy={busyKey === key && busyKind === 'restore'}
                        title="Restore"
                      >
                        <Eye className="h-3.5 w-3.5" aria-hidden />
                      </IconAction>
                    ) : (
                      <>
                        <IconAction
                          onClick={(e) => {
                            e.stopPropagation();
                            onShortlist(m);
                          }}
                          busy={busyKey === key && busyKind === 'shortlist'}
                          title={
                            shortlisted
                              ? `Already ${saveLabel.toLowerCase()}ed`
                              : saveLabel
                          }
                          active={shortlisted}
                        >
                          {shortlisted ? (
                            <Star className="h-3.5 w-3.5" aria-hidden />
                          ) : (
                            <StarOff className="h-3.5 w-3.5" aria-hidden />
                          )}
                        </IconAction>
                        {tab === 'shortlisted' ? (
                          <>
                            <IconAction
                              onClick={(e) => {
                                e.stopPropagation();
                                onMarkAsSeen?.(m);
                              }}
                              busy={false}
                              title={seen ? 'Mark as unseen' : 'Mark as seen'}
                              active={seen}
                            >
                              {seen ? (
                                <EyeOff className="h-3.5 w-3.5" aria-hidden />
                              ) : (
                                <Eye className="h-3.5 w-3.5" aria-hidden />
                              )}
                            </IconAction>
                            <IconAction
                              onClick={(e) => {
                                e.stopPropagation();
                                onRemove?.(m);
                              }}
                              busy={busyKey === key && busyKind === 'remove'}
                              title="Remove from list"
                            >
                              <StarOff className="h-3.5 w-3.5" aria-hidden />
                            </IconAction>
                          </>
                        ) : (
                          <IconAction
                            onClick={(e) => {
                              e.stopPropagation();
                              onHide(m);
                            }}
                            busy={busyKey === key && busyKind === 'hide'}
                            title="Hide"
                          >
                            <EyeOff className="h-3.5 w-3.5" aria-hidden />
                          </IconAction>
                        )}
                      </>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpen(m);
                      }}
                      className="inline-flex h-6 items-center rounded-[6px] border px-2 text-[11px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
                      style={{
                        borderColor: 'var(--vscode-border-primary)',
                        color: 'var(--vscode-text-secondary)',
                      }}
                    >
                      Open
                    </button>
                  </div>
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Th({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      scope="col"
      className={`whitespace-nowrap px-3 py-2 font-semibold ${className}`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <td className={`max-w-[180px] px-3 py-2 align-middle ${className}`}>
      {children}
    </td>
  );
}

function ChipStack({
  items,
  limit,
  tone,
}: {
  items: string[];
  limit: number;
  tone: 'ok' | 'warn';
}) {
  if (items.length === 0) {
    return (
      <span style={{ color: 'var(--vscode-text-tertiary)' }}>—</span>
    );
  }
  const shown = items.slice(0, limit);
  const rest = items.length - shown.length;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {shown.map((s) => (
        <span
          key={s}
          className="inline-flex h-5 items-center rounded-full px-1.5 text-[10px]"
          style={{
            background: tone === 'ok' ? '#E0F2FE' : '#F1F5F9',
            color: tone === 'ok' ? '#0369A1' : '#64748B',
          }}
        >
          {s}
        </span>
      ))}
      {rest > 0 ? (
        <span
          className="text-[10px]"
          style={{ color: 'var(--vscode-text-tertiary)' }}
        >
          +{rest}
        </span>
      ) : null}
    </div>
  );
}

function IconAction({
  children,
  onClick,
  busy,
  title,
  active,
}: {
  children: React.ReactNode;
  onClick: (e: React.MouseEvent) => void;
  busy?: boolean;
  title: string;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      title={title}
      aria-label={title}
      className="inline-flex h-6 w-6 items-center justify-center rounded-[6px] border transition-colors hover:bg-[var(--vscode-bg-hover)] disabled:cursor-not-allowed disabled:opacity-50"
      style={{
        borderColor: 'var(--vscode-border-primary)',
        color: active ? '#065F46' : 'var(--vscode-text-secondary)',
        background: active ? 'var(--ggj-accent-soft,#D1FAE5)' : 'transparent',
      }}
    >
      {busy ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : children}
    </button>
  );
}

function PaginationFooter({
  total,
  page,
  pageSize,
  totalPages,
  onPageChange,
  onPageSizeChange,
}: {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  onPageChange: (n: number) => void;
  onPageSizeChange: (n: number) => void;
}) {
  const start = total === 0 ? 0 : page * pageSize + 1;
  const end = Math.min(total, (page + 1) * pageSize);
  return (
    <div
      className="mt-3 flex flex-wrap items-center justify-between gap-2 text-[11px]"
      style={{ color: 'var(--vscode-text-secondary)' }}
    >
      <div className="flex items-center gap-2">
        <span>Rows per page</span>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="h-7 rounded-[6px] border px-1 text-[11px] outline-none"
          style={{
            borderColor: 'var(--vscode-border-primary)',
            background: 'var(--vscode-bg-primary)',
            color: 'var(--vscode-text-primary)',
          }}
          aria-label="Rows per page"
        >
          {PAGE_SIZE_OPTIONS.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <span className="tabular-nums">
          {start}–{end} of {total}
        </span>
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(0, page - 1))}
          disabled={page === 0}
          className="inline-flex h-7 w-7 items-center justify-center rounded-[6px] border transition-colors hover:bg-[var(--vscode-bg-hover)] disabled:cursor-not-allowed disabled:opacity-40"
          style={{
            borderColor: 'var(--vscode-border-primary)',
            color: 'var(--vscode-text-secondary)',
          }}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
        </button>
        <span className="tabular-nums" style={{ color: 'var(--vscode-text-primary)' }}>
          {page + 1} / {totalPages}
        </span>
        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
          disabled={page >= totalPages - 1}
          className="inline-flex h-7 w-7 items-center justify-center rounded-[6px] border transition-colors hover:bg-[var(--vscode-bg-hover)] disabled:cursor-not-allowed disabled:opacity-40"
          style={{
            borderColor: 'var(--vscode-border-primary)',
            color: 'var(--vscode-text-secondary)',
          }}
          aria-label="Next page"
        >
          <ChevronRight className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
    </div>
  );
}

function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0] ?? '')
    .join('')
    .toUpperCase();
}
